/**
 * 管理员「图表风格」管理页面 —— 独立于技能，与生图引擎(mermaid)配套。
 * config_json = mermaid.initialize 配置（配色+线型），guidance = 画法指导。
 */
import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/adminApi';
import { confirm } from '../../store/toastStore';

interface DiagramStyle {
  id: number;
  name: string;
  description: string;
  config_json: string;
  guidance: string;
  is_builtin: boolean;
  is_active: boolean;
  sort_order: number;
}

const emptyForm = {
  name: '',
  description: '',
  config_json: '{"theme":"base","themeVariables":{"primaryColor":"#1e3a8a","primaryTextColor":"#ffffff","lineColor":"#1e40af"},"flowchart":{"curve":"linear","htmlLabels":true}}',
  guidance: '',
  is_active: true,
  sort_order: 0,
};

const CURVE_LABEL: Record<string, string> = {
  step: '直角折线', basis: '平滑曲线', cardinal: '柔和曲线', monotone: '平滑受控', linear: '直线',
};

const AdminDiagramStyles: React.FC = () => {
  const [styles, setStyles] = useState<DiagramStyle[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<DiagramStyle | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ ...emptyForm });

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.getDiagramStyles();
      setStyles(res.data);
    } catch {
      setError('加载图表风格失败');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ ...emptyForm }); setEditing(null); setShowCreate(false); setError(''); };

  const handleEdit = (s: DiagramStyle) => {
    setEditing(s);
    setForm({
      name: s.name, description: s.description || '', config_json: s.config_json,
      guidance: s.guidance || '', is_active: s.is_active, sort_order: s.sort_order,
    });
    setShowCreate(false); setError('');
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('请填写名称'); return; }
    try { JSON.parse(form.config_json); } catch { setError('配置 JSON 格式错误'); return; }
    setSaving(true); setError('');
    try {
      if (editing) await adminApi.updateDiagramStyle(editing.id, form);
      else await adminApi.createDiagramStyle(form);
      resetForm(); await load();
    } catch (err: any) {
      setError(err.response?.data?.detail || '保存失败');
    } finally { setSaving(false); }
  };

  const handleDelete = async (s: DiagramStyle) => {
    if (s.is_builtin) { setError('内置风格不可删除，可改为禁用'); return; }
    if (!await confirm({ title: '确认删除', message: `确定删除图表风格「${s.name}」吗？`, confirmLabel: '删除', danger: true })) return;
    try { await adminApi.deleteDiagramStyle(s.id); load(); }
    catch (err: any) { setError(err.response?.data?.detail || '删除失败'); }
  };

  const curveOf = (cfg: string) => {
    try { const c = JSON.parse(cfg)?.flowchart?.curve; return c ? (CURVE_LABEL[c] || c) : '默认'; } catch { return '-'; }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-foreground">图表风格管理</h1>
          <p className="text-xs text-muted-foreground mt-1">与生图引擎(Mermaid)配套；config 控制配色与连线线型，与「技能」相互独立。</p>
        </div>
        <button onClick={() => { resetForm(); setShowCreate(true); }}
          className="px-4 py-2 bg-primary hover:bg-primary/80 text-white text-sm font-semibold rounded-xl transition-colors">
          + 新建风格
        </button>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{error}</div>}

      {(showCreate || editing) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={resetForm} />
          <div className="relative bg-card rounded-2xl border border-border shadow-[0_20px_60px_rgba(0,0,0,0.15)] w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-foreground mb-4">{editing ? '编辑图表风格' : '新建图表风格'}</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">名称</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary" placeholder="如：商务蓝" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">描述</label>
                <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary" placeholder="如：深蓝+直角折线" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Mermaid 配置 JSON（theme/themeVariables/flowchart.curve）</label>
                <textarea value={form.config_json} onChange={e => setForm({ ...form, config_json: e.target.value })} rows={6}
                  className="w-full px-3 py-2 border border-border rounded-xl text-sm font-mono focus:outline-none focus:border-primary" />
                <p className="text-xs text-muted-foreground mt-1">
                  线型 flowchart.curve 可选：step(直角折线) / basis(平滑) / cardinal(柔和) / monotone(平滑受控) / linear(直线)
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">画法指导（追加进生图提示词，告诉 AI 箭头/节点约定）</label>
                <textarea value={form.guidance} onChange={e => setForm({ ...form, guidance: e.target.value })} rows={3}
                  className="w-full px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary" placeholder="如：主流程用实线箭头，关键依赖用粗箭头 ==>" />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 text-primary border-border rounded" />
                  启用
                </label>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">排序</label>
                  <input type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                    className="w-20 px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary" />
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={resetForm} className="px-4 py-2 text-sm text-foreground bg-accent rounded-xl hover:bg-accent">取消</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-primary hover:bg-primary/80 text-white text-sm font-semibold rounded-xl disabled:opacity-50">
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12 text-muted-foreground">加载中...</div>
      ) : (
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-background border-b border-border">
              <tr>
                <th className="text-left px-6 py-3 font-medium text-muted-foreground">名称</th>
                <th className="text-left px-6 py-3 font-medium text-muted-foreground">描述</th>
                <th className="text-left px-6 py-3 font-medium text-muted-foreground">线型</th>
                <th className="text-left px-6 py-3 font-medium text-muted-foreground">排序</th>
                <th className="text-left px-6 py-3 font-medium text-muted-foreground">状态</th>
                <th className="text-right px-6 py-3 font-medium text-muted-foreground">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {styles.map(s => (
                <tr key={s.id} className="hover:bg-accent">
                  <td className="px-6 py-3 font-medium text-foreground">
                    {s.name}{s.is_builtin && <span className="ml-2 text-[10px] text-muted-foreground border border-border rounded px-1">内置</span>}
                  </td>
                  <td className="px-6 py-3 text-muted-foreground">{s.description || '-'}</td>
                  <td className="px-6 py-3 text-muted-foreground">{curveOf(s.config_json)}</td>
                  <td className="px-6 py-3 text-muted-foreground">{s.sort_order}</td>
                  <td className="px-6 py-3">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${s.is_active ? 'bg-green-50 text-green-700' : 'bg-accent text-muted-foreground'}`}>
                      {s.is_active ? '启用' : '禁用'}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <button onClick={() => handleEdit(s)} className="text-primary hover:underline text-xs mr-3">编辑</button>
                    <button onClick={() => handleDelete(s)} className="text-red-500 hover:underline text-xs">删除</button>
                  </td>
                </tr>
              ))}
              {styles.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">暂无图表风格</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminDiagramStyles;
